import React, { useCallback, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import TranslationVerification from './TranslationVerification';
import LanguageSelection from './LanguageSelection';
import LoginForm from './LoginForm';
import './App.css'; // Ensure App.css is correctly imported
import {
  endReadOnlySession,
  getElderVerificationCounts,
  handleSessionError,
  isProxyDataSource,
  isReadOnlyDataSource,
} from './Airtable';

const App = () => {
  const readOnlyPreview = isReadOnlyDataSource();
  const cloudflarePreview = isProxyDataSource();
  const [elder, setElder] = useState(null);
  const [language, setLanguage] = useState(null);
  const [languageId, setLanguageId] = useState(null); // New state for language ID
  const [languageUuid, setLanguageUuid] = useState(null); // New state for language UUID
  const [backgroundImage, setBackgroundImage] = useState('');
  const [totalVerified, setTotalVerified] = useState(null);
  const [todayVerified, setTodayVerified] = useState(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsError, setCountsError] = useState('');
  const [countsAttempt, setCountsAttempt] = useState(0);

  useEffect(() => {
    const currentDate = new Date();
    const month = currentDate.getMonth() + 1; // getMonth() returns 0-indexed month

    let background = '';
    if (month >= 4 && month <= 8) {
      background = 'spring.jpg';
    } else if (month >= 9 && month <= 10) {
      background = 'fall.jpg';
    } else {
      background = 'winter.jpg';
    }

    setBackgroundImage(background);
  }, []);

  const handleLogout = useCallback(async () => {
    const logoutRequest = endReadOnlySession();
    setElder(null);
    setLanguage(null);
    setLanguageId(null);
    setLanguageUuid(null);
    setTotalVerified(null);
    setTodayVerified(null);
    setCountsLoading(false);
    setCountsError('');
    setCountsAttempt(0);
    localStorage.removeItem('email');
    try {
      await logoutRequest;
    } catch (error) {
      console.error('Unable to close the session:', error);
    }
  }, []);

  const handleCountsLoaded = useCallback(() => {
    setCountsError('');
  }, []);

  const handleCountsError = useCallback(() => {
    setCountsError('Verification counts are temporarily unavailable.');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchVerifiedCount = async () => {
      if (elder) {
        setTotalVerified(null);
        setTodayVerified(null);
        setCountsLoading(true);
        setCountsError('');
        try {
          const counts = await getElderVerificationCounts(elder.id);
          if (cancelled) return;
          setTotalVerified(counts.totalVerified);
          setTodayVerified(counts.totalVerifiedToday);
        } catch (error) {
          if (cancelled) return;
          console.error('Unable to load verification counts:', error);
          if (await handleSessionError(error, handleLogout)) return;
          setCountsError('Verification counts are temporarily unavailable.');
        } finally {
          if (!cancelled) setCountsLoading(false);
        }
      }
    };

    fetchVerifiedCount();
    return () => {
      cancelled = true;
    };
  }, [countsAttempt, elder, handleLogout]);

  const handleLogin = (elder) => {
    setElder(elder);
  };

  const handleLanguageSelect = (language, id, uuid) => {
    if (
      typeof language !== 'string' || language.trim().length === 0
      || typeof id !== 'string' || id.trim().length === 0
      || typeof uuid !== 'string' || uuid.trim().length === 0
    ) {
      console.error('Unable to select a language with invalid identifiers.');
      return;
    }
    setLanguage(language.trim());
    setLanguageId(id.trim());
    setLanguageUuid(uuid.trim());
  };

  const handleSwitchLanguage = () => {
    setLanguage(null);
    setLanguageId(null); // Reset language ID on switch
    setLanguageUuid(null); // Reset language UUID on switch
  };

  const isVerificationStep = elder && language && languageId && languageUuid;

  return (
    <div
      className={`app${readOnlyPreview ? ' read-only-preview' : ''}`}
      style={{
        backgroundImage: !isVerificationStep ? `url(${backgroundImage})` : 'none',
      }}
    >
      <Helmet>
        <title>Connected North - Language Verification Platform</title>
      </Helmet>
      {(isVerificationStep || ((cloudflarePreview || readOnlyPreview) && elder)) && (
        <div className="header">
          {isVerificationStep && (
            <button onClick={handleSwitchLanguage} className="header-button">Switch Language</button>
          )}
          <button onClick={handleLogout} className="header-button">Logout</button>
        </div>
      )}
      {!elder ? (
        <LoginForm onLogin={handleLogin} />
      ) : !language || !languageId || !languageUuid ? (
        <LanguageSelection elder={elder} onSelectLanguage={handleLanguageSelect} onLogout={handleLogout} />
      ) : (
        <>
          <TranslationVerification
            elder={elder}
            language={language}
            languageId={languageId} // Pass language ID to TranslationVerification
            languageUuid={languageUuid} // Pass language UUID to TranslationVerification
            onLogout={handleLogout}
            onSwitchLanguage={handleSwitchLanguage}
            setTotalVerified={setTotalVerified}   // Pass setTotalVerified
            setTodayVerified={setTodayVerified}   // Pass setTodayVerified
            onCountsLoaded={handleCountsLoaded}
            onCountsError={handleCountsError}
            readOnly={readOnlyPreview}
          />
          <div className="status-bar">
            {countsLoading ? (
              'Loading verification counts…'
            ) : countsError || totalVerified === null || todayVerified === null ? (
              <span className="status-error" role="alert">
                {totalVerified !== null && todayVerified !== null && (
                  <>Phrases Verified: {totalVerified} (Total) {todayVerified} (Today). </>
                )}
                {countsError || 'Verification counts are temporarily unavailable.'}{' '}
                <button type="button" onClick={() => setCountsAttempt(attempt => attempt + 1)}>
                  Retry counts
                </button>
              </span>
            ) : (
              <>Phrases Verified: {totalVerified} (Total) {todayVerified} (Today)</>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default App;
